import { toAnalyticsPageUrl } from '@/lib/analyticsPageUrl';
import type { PageContext, TrimmedPageContext } from '@/lib/pageContext';
import { isRecord } from '@/lib/utils/isRecord';

/**
 * The AWS WAF managed rule set inspects at most this many bytes of a request body,
 * and the limit is not raisable on an ALB. A larger body is rejected at the edge, so
 * this bounds the whole payload rather than any one field.
 */
export const MAX_PAGE_VIEW_BODY_BYTES = 8_192;

export function serializedBytes(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).length;
}

/** The Open Graph price, availability, condition and publish-time tags are absent
 * for the same reason their JSON-LD counterparts are blocklisted below: beacon keys
 * its prompt cache on content that excludes them, so sending them spends the byte
 * budget on something it discards on arrival. */
const META_ALLOWLIST = new Set<string>([
  'description',
  'keywords',
  'og:title',
  'og:site_name',
  'og:description',
  'og:type',
  'product:brand',
  'product:category',
  'twitter:label1',
  'twitter:data1',
  'twitter:label2',
  'twitter:data2',
  'article:section',
  'article:tag',
  'dc.title',
  'dc.description',
  'dc.subject',
]);

/** JSON-LD node `@type`s worth keeping; all other nodes are dropped whole.
 *
 * Matched case-insensitively, as `pickMeta` and the key blocklist are. JSON-LD
 * term matching is case-sensitive and `product` is strictly not the schema.org
 * term `Product`, but a page that mis-cases its markup still describes a real
 * product, and an exact match drops the node and its page-type signal with it. */
const JSONLD_TYPE_ALLOWLIST = new Set<string>(
  [
    'Product',
    'ProductGroup',
    'Offer',
    'AggregateOffer',
    'BreadcrumbList',
    'ItemList',
    'CollectionPage',
    'FAQPage',
    'QAPage',
    'Organization',
    'WebSite',
    'Article',
    'BlogPosting',
    'Recipe',
    'HowTo',
    'MerchantReturnPolicy',
    // Beacon classifies a page as `search` from this node.
    'SearchResultsPage',
  ].map((type) => type.toLowerCase()),
);

/** Keys removed from within a kept JSON-LD node: bulky (`review`, `image`),
 * identity/social/search plumbing that gives the model nothing, PII-bearing
 * contact fields (a kept Organization/Article node routinely nests these), and
 * raw URL fields (`url`, `mainEntityOfPage`) — like the top-level url they can
 * carry query-string PII, and they aren't prompt signal.
 *
 * Matched case-insensitively, as `pickMeta` does: schema.org spells these one way but a
 * page emits whatever it likes, and an exact match would let `Email` through a list whose
 * job is to keep contact details out. */
const JSONLD_KEY_BLOCKLIST = new Set<string>(
  [
    'review',
    'image',
    '@context',
    '@graph',
    '@id',
    'logo',
    'sameAs',
    'potentialAction',
    'email',
    'telephone',
    'address',
    'contactPoint',
    'author',
    'url',
    'mainEntityOfPage',
    'contentUrl',
    'embedUrl',
    'merchantReturnLink',
    '__proto__',
    'constructor',
    // Volatile: a price, a rating, a stock flag. Beacon excludes these from the
    // content it keys its prompt cache on, so a prompt generated from one would be
    // stale as soon as the page repriced. Dropping them here spends the byte budget
    // on content beacon actually keeps.
    'availability',
    'bestRating',
    'highPrice',
    'inventoryLevel',
    'itemCondition',
    'lowPrice',
    'offerCount',
    'price',
    'priceCurrency',
    'priceValidUntil',
    'ratingCount',
    'ratingValue',
    'reviewCount',
    'validFrom',
    'worstRating',
  ].map((key) => key.toLowerCase()),
);

/**
 * `maxBytes` is what the payload has left after its other fields, measured rather
 * than reserved — `pageIdentity` and `queryKeys` are page-controlled and a fixed
 * headroom guess is wrong for a long URL.
 */
export function trimPageContext(context: PageContext, maxBytes: number): TrimmedPageContext {
  const trimmed: TrimmedPageContext = {
    // Scrub canonicalUrl to origin+pathname: the raw canonical can carry
    // query/fragment PII (tokens, emails). `url` is dropped entirely — it's
    // redundant with the message's pageIdentity.
    canonicalUrl: context.canonicalUrl ? toAnalyticsPageUrl(context.canonicalUrl) || null : null,
    title: context.title,
    meta: pickMeta(context.meta),
    jsonLd: context.jsonLd.map(trimJsonLdNode).filter((node): node is unknown => node !== undefined),
  };
  return backstopTruncate(trimmed, maxBytes);
}

// Lower-cased on the way in: DCMI spells its tags `DC.description`, and some CMSes emit
// `Description`, so an exact match drops the very fields the allowlist names.
function pickMeta(meta: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(meta)) {
    const name = key.toLowerCase();
    if (META_ALLOWLIST.has(name) && out[name] === undefined) out[name] = value;
  }
  return out;
}

function trimJsonLdNode(node: unknown): unknown {
  if (!isRecord(node)) return undefined;
  const types = normalizeTypes(node['@type']);
  if (!types.some((t) => JSONLD_TYPE_ALLOWLIST.has(t))) return undefined;
  return stripBlockedKeys(node);
}

function normalizeTypes(rawType: unknown): string[] {
  if (typeof rawType === 'string') return [rawType.toLowerCase()];
  if (Array.isArray(rawType))
    return rawType.filter((t): t is string => typeof t === 'string').map((t) => t.toLowerCase());
  return [];
}

// JSON.parse is iterative and this is not, so a page can hand us a tree it parsed happily
// that blows the stack here. Real JSON-LD nests a handful of levels.
const MAX_JSONLD_DEPTH = 64;

/** Recursive because the size wins are in nested `review`/`image`, e.g. under `offers`. */
function stripBlockedKeys(value: unknown, depth = 0): unknown {
  if (depth > MAX_JSONLD_DEPTH) return undefined;
  // Filtered, not just mapped: a depth bail returns undefined, which an object slot drops
  // on its own but an array slot keeps as a null. Nothing parsed from JSON is undefined,
  // so this only ever removes what the bail shed.
  if (Array.isArray(value))
    return value.map((entry) => stripBlockedKeys(entry, depth + 1)).filter((entry) => entry !== undefined);
  if (!isRecord(value)) return value;
  // Null prototype: `out[key] = v` where the page supplied a `__proto__` key would
  // otherwise hit the setter and hand the sanitized node an attacker's prototype.
  const out: Record<string, unknown> = Object.create(null);
  for (const [key, v] of Object.entries(value)) {
    const name = key.toLowerCase();
    if (JSONLD_KEY_BLOCKLIST.has(name)) continue;
    // A string `item` is a raw URL; an object `item` holds the breadcrumb label, and
    // dropping both leaves a ListItem carrying nothing but its position.
    if (name === 'item' && typeof v === 'string') continue;
    out[key] = stripBlockedKeys(v, depth + 1);
  }
  return out;
}

/**
 * The page's own summary, second only to the title, so these are held back from the
 * meta sweep. Most authoritative first — they routinely carry the same prose, so the
 * lower-priority ones are shed whole rather than every copy being truncated.
 */
const SUMMARY_META_KEYS = ['description', 'og:description', 'dc.description'] as const;
const SUMMARY_META_KEY_SET: ReadonlySet<string> = new Set(SUMMARY_META_KEYS);

/**
 * Sheds in ascending order of signal per byte, truncating the summary and title rather
 * than dropping them so a shed page still says what it is. A context can still exceed a
 * budget smaller than its own empty shape; the caller treats that as a page it cannot
 * describe.
 */
function backstopTruncate(context: TrimmedPageContext, maxSize: number): TrimmedPageContext {
  let shed = context;
  const fits = (candidate: TrimmedPageContext) => serializedBytes(candidate) <= maxSize;
  if (fits(shed)) return shed;

  // Cost each node once and subtract, rather than re-serializing the whole context per
  // pop. A collection page carries a node per product card, and this runs synchronously
  // on the host page's main thread — the quadratic form measured 28ms at 500 nodes.
  const nodeBytes = shed.jsonLd.map(serializedBytes);
  const withoutNodes = serializedBytes({ ...shed, jsonLd: [] });
  let keep = nodeBytes.length;
  let nodeTotal = nodeBytes.reduce((total, bytes) => total + bytes, 0);
  // Exact, not approximate: N nodes cost N-1 separators, and rounding up here would
  // shed a node that fits.
  const projected = () => withoutNodes + nodeTotal + Math.max(0, keep - 1);
  while (keep > 0 && projected() > maxSize) {
    keep -= 1;
    nodeTotal -= nodeBytes[keep];
  }
  const jsonLd = shed.jsonLd.slice(0, keep);
  // Belt and braces: the arithmetic above is exact for well-formed nodes, so this
  // settles anything that serializes differently inside an array than alone.
  while (jsonLd.length > 0 && !fits({ ...shed, jsonLd })) {
    jsonLd.pop();
  }
  shed = { ...shed, jsonLd };
  if (fits(shed)) return shed;

  const meta = { ...shed.meta };
  const summaries = SUMMARY_META_KEYS.filter((key) => meta[key] !== undefined);
  const sweepable = Object.keys(meta)
    .filter((key) => !SUMMARY_META_KEY_SET.has(key))
    .sort((a, b) => meta[b].length - meta[a].length);
  for (const key of sweepable) {
    delete meta[key];
    shed = { ...shed, meta };
    if (fits(shed)) return shed;
  }

  for (const key of summaries.slice(1).reverse()) {
    delete meta[key];
    shed = { ...shed, meta };
    if (fits(shed)) return shed;
  }

  // Before any prose is cut: after scrubbing this is origin+pathname, which the payload
  // already carries as `pageIdentity`, so it is the cheapest thing left to lose.
  if (shed.canonicalUrl !== null) {
    shed = { ...shed, canonicalUrl: null };
    if (fits(shed)) return shed;
  }

  const survivor = summaries[0];
  if (survivor !== undefined) {
    const truncated = truncateToFit(meta[survivor], (candidate) =>
      fits({ ...shed, meta: { ...meta, [survivor]: candidate } }),
    );
    if (truncated.length > 0) meta[survivor] = truncated;
    else delete meta[survivor];
    shed = { ...shed, meta };
    if (fits(shed)) return shed;
  }

  return { ...shed, title: truncateToFit(shed.title, (candidate) => fits({ ...shed, title: candidate })) };
}

/**
 * Longest prefix of `text` that satisfies `fits`, cut back to a word boundary.
 * Binary search rather than an index computed from the budget: bytes do not map
 * to a character index once the text is multibyte.
 */
function truncateToFit(text: string, fits: (candidate: string) => boolean): string {
  if (fits(text)) return text;
  let shortest = 0;
  let longest = text.length;
  while (shortest < longest) {
    const midpoint = Math.ceil((shortest + longest) / 2);
    if (fits(text.slice(0, midpoint))) shortest = midpoint;
    else longest = midpoint - 1;
  }
  // A cut landing on whitespace already sits between words; stripping again there
  // would discard a whole word that fit.
  const cutMidWord = shortest > 0 && !/\s/.test(text.charAt(shortest));
  const kept = cutMidWord ? text.slice(0, shortest).replace(/\s+\S*$/, '') : text.slice(0, shortest);
  // A single word longer than the budget has no boundary to fall back to.
  return (kept.length > 0 ? kept : text.slice(0, shortest)).trimEnd();
}
